import type { SessionReport } from '@swarasense/shared';
import type { PracticeSession } from './practice-history.js';

/**
 * Phase A personalization, pure layer: turn raw inputs (a voice range, a
 * session analysis report) into the shapes `profile.ts`/`practice-history.ts`
 * persist. No I/O, no `Date.now`/`Math.random`/mic access here — callers
 * (web app code) own the clock and ids so these stay trivially unit-testable
 * and deterministic.
 */

/** 12-TET pitch classes, sharps-only spelling — the same convention
 * `settings-popover.ts`'s Sa picker uses (`PITCH_CLASSES`), kept as our own
 * copy here since this module must stay dependency-free/pure. */
const PITCH_CLASSES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;

/**
 * Nearest 12-TET note (pitch class + octave) to a frequency, using the same
 * octave numbering `noteToFrequency` (shared/music.ts) does — A4 = 440 Hz,
 * so this is exactly its inverse (round-tripping through `noteToFrequency`
 * recovers a frequency within half a semitone of `hz`). Needed because the
 * rest of the app keys Sa off `{pitchClass, octave}`, not raw Hz.
 */
export function hzToNearestNote(hz: number): { pitchClass: string; octave: number } {
  const midi = Math.round(69 + 12 * Math.log2(hz / 440));
  const pitchClass = PITCH_CLASSES[((midi % 12) + 12) % 12]!;
  const octave = Math.floor(midi / 12) - 1;
  return { pitchClass, octave };
}

/**
 * Recommend a Sa (tonic) from a singer's comfortable low-to-high vocal
 * range. No AI/audio analysis — a plain, documented arithmetic heuristic:
 *
 *  - If the range spans at least an octave (`rangeCents >= 1200`), place Sa
 *    near the LOWER THIRD of the range (`rangeCents / 3` above the bottom).
 *    That leaves room both below Sa (for komal swaras dipping under it) and,
 *    just as importantly, keeps a full octave ABOVE Sa (Sa..Ṡ, what most
 *    practice material spans) inside the reported range. If a straight
 *    lower-third placement would push Sa+1200¢ past the top of the range
 *    (true for a range only a little over an octave), Sa is pulled down just
 *    far enough that Sa..Sa+1200¢ still fits: `saCents = min(rangeCents/3,
 *    rangeCents-1200)`.
 *  - If the range is UNDER an octave, there's no way to fit a full Sa..Ṡ
 *    span in it at all: Sa goes at the low end (as low as they can
 *    comfortably sing), and `tightRange:true` flags that the range should
 *    grow with practice before Sa can be placed more generously.
 *
 * Pure and deterministic: a function of the two Hz bounds only.
 */
export function recommendSa(
  lowHz: number,
  highHz: number,
): { pitchClass: string; octave: number; tightRange: boolean } {
  const rangeCents = 1200 * Math.log2(highHz / lowHz);
  const tightRange = rangeCents < 1200;
  const saCents = tightRange ? 0 : Math.max(0, Math.min(rangeCents / 3, rangeCents - 1200));
  const saHz = lowHz * 2 ** (saCents / 1200);
  return { ...hzToNearestNote(saHz), tightRange };
}

/**
 * Map a completed `analyzeSession` report + its practice context into the
 * `PracticeSession` history shape (minus `id`, which the history store
 * generates on write). `weakestSwara` is omitted (not set to `undefined`,
 * per `exactOptionalPropertyTypes`) when the report has no meaningful
 * per-swara stats (e.g. a very short/empty take).
 */
export function sessionFromReport(
  report: SessionReport,
  ctx: {
    raga: string;
    level: string;
    mode: 'free' | 'drill' | 'ear';
    dateISO: string;
    durationSec?: number;
  },
): Omit<PracticeSession, 'id'> {
  const weakestSwara = report.weakest[0]?.symbol;
  const durationSec = ctx.durationSec ?? Math.round(report.durationSec);
  return {
    dateISO: ctx.dateISO,
    raga: ctx.raga,
    level: ctx.level,
    mode: ctx.mode,
    inRagaPct: Math.round(report.inRagaPct),
    wellCentredPct: Math.round(report.centeredPct),
    gamakaAdherencePct: Math.round(report.gamaka.adherencePct),
    ...(weakestSwara !== undefined ? { weakestSwara } : {}),
    durationSec,
  };
}
